import { db, functions } from "./firebase";
import { httpsCallable } from "firebase/functions";
import {
  collection,
  deleteDoc,
  deleteField,
  doc,
  getDoc,
  getDocs,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
} from "firebase/firestore";

const DEFAULT_TARGETS = { calories: 2000, protein: 150, carbs: 200, fat: 65 };
const SEARCH_USDA_FOODS = httpsCallable(functions, "searchUsdaFoods");
const LOOKUP_USDA_FOOD_BY_BARCODE = httpsCallable(functions, "lookupUsdaFoodByBarcode");
const GET_USDA_FOOD_DETAILS = httpsCallable(functions, "getUsdaFoodDetails");
const PARSE_VOICE_FOOD_LOG = httpsCallable(functions, "parseVoiceFoodLog");
const PARSE_NUTRITION_LABEL = httpsCallable(functions, "parseNutritionLabel");

function roundMacro(value) {
  return Math.round((Number(value || 0) + Number.EPSILON) * 10) / 10;
}

function normalizeText(value, fallback = "") {
  return String(value || fallback).trim();
}

function sanitizePer100g(per100g = {}) {
  return {
    calories: Math.round(Number(per100g.calories || 0)),
    protein: roundMacro(per100g.protein),
    carbs: roundMacro(per100g.carbs),
    fat: roundMacro(per100g.fat),
  };
}

function roundServingValue(value) {
  return Math.round((Number(value || 0) + Number.EPSILON) * 100) / 100;
}

function normalizeServingOptions(food = {}) {
  const rawOptions = Array.isArray(food.servingOptions) ? food.servingOptions : [];
  const seen = new Set();
  const normalized = [];

  for (const option of rawOptions) {
    const grams = Number(option?.grams || 0);
    const label = normalizeText(option?.label || "");
    if (!(grams > 0) || !label) continue;
    const key = `${label.toLowerCase()}_${grams}`;
    if (seen.has(key)) continue;
    seen.add(key);
    normalized.push({
      id: normalizeText(option?.id || key),
      label,
      grams: roundServingValue(grams),
      isDefault: Boolean(option?.isDefault),
    });
  }

  const fallbackServingSize = Number(food.servingSize || 0);
  const hasSameGramOption = normalized.some((o) => Math.abs(o.grams - fallbackServingSize) < 0.5);
  if (fallbackServingSize > 0 && !hasSameGramOption) {
    const fallbackLabel = food.source === "custom" ? `1 serving (${fallbackServingSize} g)` : `${fallbackServingSize} g`;
    const key = `${fallbackLabel.toLowerCase()}_${fallbackServingSize}`;
    normalized.unshift({
      id: key,
      label: fallbackLabel,
      grams: roundServingValue(fallbackServingSize),
      isDefault: true,
    });
  }

  if (!normalized.some((option) => option.grams === 100)) {
    normalized.push({
      id: "grams_100",
      label: "100 g",
      grams: 100,
      isDefault: normalized.length === 0,
    });
  }

  if (!normalized.some((option) => option.isDefault)) {
    normalized[0].isDefault = true;
  }

  // If the user previously logged this food with a specific serving, restore it as default
  const preferred = Number(food.preferredServingGrams || 0);
  if (preferred > 0) {
    const match = normalized.find((o) => Math.abs(o.grams - preferred) < 1);
    if (match) {
      normalized.forEach((o) => { o.isDefault = false; });
      match.isDefault = true;
    }
  }

  return normalized;
}

export function normalizeFood(food = {}) {
  const servingSize = Number(food.servingSize || 100);
  const preferredServingGrams = Number(food.preferredServingGrams || 0) || null;
  const normalized = {
    source: normalizeText(food.source, "custom"),
    sourceId: normalizeText(food.sourceId || food.fdcId || food.id || ""),
    fdcId: food.fdcId || null,
    name: normalizeText(food.name, "Unknown food"),
    brand: normalizeText(food.brand || ""),
    barcode: normalizeText(food.barcode || ""),
    dataType: normalizeText(food.dataType || ""),
    servingSize: servingSize > 0 ? servingSize : 100,
    servingUnit: normalizeText(food.servingUnit, "g"),
    per100g: sanitizePer100g(food.per100g),
    preferredServingGrams,
  };

  const servingOptions = normalizeServingOptions({
    ...food,
    servingSize: normalized.servingSize,
    preferredServingGrams,
  });

  return {
    ...normalized,
    id: food.id || buildFoodKey(normalized),
    servingOptions,
    calories: Math.round((normalized.per100g.calories * normalized.servingSize) / 100),
    protein: roundMacro((normalized.per100g.protein * normalized.servingSize) / 100),
    carbs: roundMacro((normalized.per100g.carbs * normalized.servingSize) / 100),
    fat: roundMacro((normalized.per100g.fat * normalized.servingSize) / 100),
  };
}

export function calcMacrosForServing(food, grams) {
  const normalized = normalizeFood(food);
  const g = parseFloat(grams) || 100;
  return {
    calories: Math.round((normalized.per100g.calories * g) / 100),
    protein: roundMacro((normalized.per100g.protein * g) / 100),
    carbs: roundMacro((normalized.per100g.carbs * g) / 100),
    fat: roundMacro((normalized.per100g.fat * g) / 100),
  };
}

export function buildFoodKey(food = {}) {
  const source = normalizeText(food.source, "food");
  const primaryId = normalizeText(food.sourceId || food.fdcId || food.barcode || "");
  if (primaryId) return `${source}_${primaryId}`;

  const fallback = normalizeText(food.name, "food")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");

  return `${source}_${fallback || "item"}`;
}

function savedFoodCollection(uid, collectionName) {
  return collection(db, "users", uid, collectionName);
}

async function loadSavedFoods(uid, collectionName) {
  const q = query(savedFoodCollection(uid, collectionName), orderBy("updatedAt", "desc"));
  const snap = await getDocs(q);
  return snap.docs.map((item) => normalizeFood({ id: item.id, ...item.data() }));
}

async function saveSavedFood(uid, collectionName, food, docId = buildFoodKey(food)) {
  const normalized = normalizeFood(food);
  await setDoc(doc(db, "users", uid, collectionName, docId), {
    ...normalized,
    updatedAt: serverTimestamp(),
  }, { merge: true });
  return docId;
}

export async function getMacroTargets(uid) {
  const snap = await getDoc(doc(db, "users", uid, "macroSettings", "targets"));
  if (!snap.exists()) return DEFAULT_TARGETS;
  return { ...DEFAULT_TARGETS, ...snap.data() };
}

export async function saveMacroTargets(uid, targets) {
  await setDoc(doc(db, "users", uid, "macroSettings", "targets"), targets, { merge: true });
}

export async function getEffectiveMacroTargets(uid, dateKey) {
  const snap = await getDoc(doc(db, "users", uid, "macroSettings", "targets"));
  const data = snap.data() || {};
  const override = data.dailyOverrides?.[dateKey] || null;
  const base = {
    calories: data.calories ?? DEFAULT_TARGETS.calories,
    protein: data.protein ?? DEFAULT_TARGETS.protein,
    carbs: data.carbs ?? DEFAULT_TARGETS.carbs,
    fat: data.fat ?? DEFAULT_TARGETS.fat,
  };
  return override ? { ...base, ...override, isOverride: true } : { ...base, isOverride: false };
}

export async function saveDailyTargetOverride(uid, dateKey, targets) {
  const ref = doc(db, "users", uid, "macroSettings", "targets");
  if (!targets) {
    await setDoc(ref, { dailyOverrides: { [dateKey]: deleteField() } }, { merge: true });
  } else {
    await setDoc(ref, { dailyOverrides: { [dateKey]: targets } }, { merge: true });
  }
}

export async function parseVoiceFoodLog(transcript) {
  const result = await PARSE_VOICE_FOOD_LOG({ transcript });
  return Array.isArray(result.data?.foods) ? result.data.foods : [];
}

export async function parseNutritionLabel(imageBase64) {
  const result = await PARSE_NUTRITION_LABEL({ imageBase64 });
  return result.data?.food || null;
}

export function getFoodLogRef(uid, dateKey) {
  return collection(db, "users", uid, "macroLog", dateKey, "entries");
}

export async function getFoodLog(uid, dateKey) {
  const q = query(getFoodLogRef(uid, dateKey), orderBy("loggedAt", "asc"));
  const snap = await getDocs(q);
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

export async function getMacroLogRange(uid, dates) {
  const results = await Promise.all(
    dates.map(async (date) => {
      const entries = await getFoodLog(uid, date);
      const totals = entries.reduce(
        (acc, e) => ({
          calories: acc.calories + (e.calories || 0),
          protein: acc.protein + (e.protein || 0),
          carbs: acc.carbs + (e.carbs || 0),
          fat: acc.fat + (e.fat || 0),
        }),
        { calories: 0, protein: 0, carbs: 0, fat: 0 }
      );
      return { date, ...totals, logged: entries.length > 0 };
    })
  );
  return results;
}

export async function addFoodEntry(uid, dateKey, entry) {
  const ref = doc(getFoodLogRef(uid, dateKey));
  await setDoc(ref, {
    ...entry,
    loggedAt: serverTimestamp(),
  });
  return ref.id;
}

export async function deleteFoodEntry(uid, dateKey, entryId) {
  await deleteDoc(doc(db, "users", uid, "macroLog", dateKey, "entries", entryId));
}

export async function searchFoodByName(queryText) {
  const query = normalizeText(queryText);
  if (!query) return [];
  const result = await SEARCH_USDA_FOODS({ query });
  return Array.isArray(result.data?.foods) ? result.data.foods.map(normalizeFood) : [];
}

function normalizeOpenFoodFactsProduct(p, barcode) {
  const n = p.nutriments || {};
  const servingGrams = Number(p.serving_quantity || 0);
  // OFF stores energy in kJ under "energy_100g"; kcal under "energy-kcal_100g"
  const cal100 = Number(n["energy-kcal_100g"] || 0) || Math.round(Number(n["energy_100g"] || 0) / 4.184);
  return {
    source: "openfoodfacts",
    sourceId: barcode,
    fdcId: null,
    dataType: "Branded",
    name: (p.product_name_en || p.product_name || "").trim() || "Unknown",
    brand: (p.brands || "").split(",")[0].trim(),
    barcode,
    servingSize: servingGrams > 0 ? servingGrams : 100,
    servingUnit: "g",
    per100g: {
      calories: Math.round(cal100),
      protein: Math.round(Number(n["proteins_100g"] || 0) * 10) / 10,
      carbs: Math.round(Number(n["carbohydrates_100g"] || 0) * 10) / 10,
      fat: Math.round(Number(n["fat_100g"] || 0) * 10) / 10,
    },
  };
}

export async function searchFoodByBarcode(barcodeText) {
  const barcode = normalizeText(barcodeText);
  if (!barcode) return null;

  // Try USDA first
  try {
    const result = await LOOKUP_USDA_FOOD_BY_BARCODE({ barcode });
    if (result.data?.food) return normalizeFood(result.data.food);
  } catch { /* fall through */ }

  // Fall back to Open Food Facts — free, 3M+ products, CORS-enabled
  try {
    const resp = await fetch(`https://world.openfoodfacts.org/api/v0/product/${encodeURIComponent(barcode)}.json`);
    if (resp.ok) {
      const data = await resp.json();
      if (data.status === 1 && data.product) {
        return normalizeFood(normalizeOpenFoodFactsProduct(data.product, barcode));
      }
    }
  } catch { /* not found */ }

  return null;
}

export async function getFoodDetails(food) {
  const normalized = normalizeFood(food);
  if (normalized.source !== "usda" || !normalized.fdcId) return normalized;
  const result = await GET_USDA_FOOD_DETAILS({ fdcId: normalized.fdcId });
  if (!result.data?.food) return normalized;
  // Preserve the user's serving preference when loading fresh details
  return normalizeFood({ ...result.data.food, preferredServingGrams: normalized.preferredServingGrams });
}

export async function getRecentFoods(uid) {
  return loadSavedFoods(uid, "macroRecentFoods");
}

export async function saveRecentFood(uid, food, servingGrams = null) {
  const normalized = normalizeFood(food);
  const docId = buildFoodKey(normalized);
  const update = { ...normalized, updatedAt: serverTimestamp() };
  if (servingGrams != null && servingGrams > 0) {
    update.preferredServingGrams = servingGrams;
  }
  await setDoc(doc(db, "users", uid, "macroRecentFoods", docId), update, { merge: true });
}

export async function getFavoriteFoods(uid) {
  return loadSavedFoods(uid, "macroFavoriteFoods");
}

export async function saveFavoriteFood(uid, food) {
  return saveSavedFood(uid, "macroFavoriteFoods", food);
}

export async function removeFavoriteFood(uid, foodOrId) {
  const docId = typeof foodOrId === "string" ? foodOrId : buildFoodKey(foodOrId);
  await deleteDoc(doc(db, "users", uid, "macroFavoriteFoods", docId));
}

export async function toggleFavoriteFood(uid, food) {
  const docId = buildFoodKey(food);
  const ref = doc(db, "users", uid, "macroFavoriteFoods", docId);
  const snap = await getDoc(ref);
  if (snap.exists()) {
    await deleteDoc(ref);
    return false;
  }

  await setDoc(ref, {
    ...normalizeFood(food),
    updatedAt: serverTimestamp(),
  });
  return true;
}

export async function getCustomFoods(uid) {
  return loadSavedFoods(uid, "macroCustomFoods");
}

export async function createCustomFood(uid, food) {
  const ref = doc(savedFoodCollection(uid, "macroCustomFoods"));
  const normalized = normalizeFood({
    ...food,
    source: "custom",
    sourceId: ref.id,
    servingOptions: [
      {
        id: "custom_serving",
        label: `1 serving (${food.servingSize || 100} g)`,
        grams: food.servingSize || 100,
        isDefault: true,
      },
      {
        id: "grams_100",
        label: "100 g",
        grams: 100,
        isDefault: false,
      },
    ],
  });

  await setDoc(ref, {
    ...normalized,
    updatedAt: serverTimestamp(),
  });

  return normalizeFood({ ...normalized, id: ref.id });
}

export async function deleteCustomFood(uid, foodOrId) {
  const docId = typeof foodOrId === "string" ? foodOrId : normalizeText(foodOrId.sourceId || foodOrId.id);
  if (!docId) return;
  await deleteDoc(doc(db, "users", uid, "macroCustomFoods", docId));
}
